import { useState, useRef } from "react";
import SVG from "react-inlinesvg";
import useOnClickOutside from "../../../hooks/useOnClickOutside";
import { StyledContainer } from "./styles";
import { useStoryBibleStore } from "../../../stores/storyBibleStore";
import classNames from "classnames";
import { useStructuredOutlineStore } from "../StructuredOutline/StructuredOutlineStore";
import { useProjectStore } from "../../../stores/projectStore";
import { useSeriesStore } from "../../../stores/seriesStore";

const OutlineSectionSelector = () => {
  const options = useStoryBibleStore((state) => state.options);
  const sceneId = useStoryBibleStore((state) => state.sceneId);
  const sceneToWrite = useStoryBibleStore((state) => state.sceneToWrite);
  const setScenes = useStoryBibleStore((state) => state.setScenes);
  const triggerImmediateSave = useStoryBibleStore((state) => state.triggerImmediateSave);
  const previousSceneLabel = useStoryBibleStore((state) => state.previousSceneLabel);
  const setPreviousSceneLabel = useStoryBibleStore((state) => state.setPreviousSceneLabel);
  const buildChapterOptions = useStructuredOutlineStore((state) => state.buildChapterOptions);
  const project = useProjectStore((state) => state.project);
  const selectedProjects = useSeriesStore((state) => state.selectedProjects);
  const [showSubmenu, setShowSubmenu] = useState(false);
  const [showIntervention, setShowIntervention] = useState(false);
  const submenuControllerRef = useRef();
  const interventionControllerRef = useRef();
  useOnClickOutside(submenuControllerRef, () => setShowSubmenu(false));
  useOnClickOutside(interventionControllerRef, () => setShowIntervention(false));
  const icon = sceneToWrite ? (
    <SVG
      key="linked"
      src={"/assets/chain-linked.svg"}
    />
  ) : (
    <SVG
      key="unlinked"
      src={"/assets/chain-unlinked.svg"}
    />
  );

  function selectStructuredOutlineChapter(chapterId) {
    return () => {
      setScenes((draft) => {
        draft[sceneId].itemId = chapterId;
      });
      triggerImmediateSave?.();
      // setPreviousSceneLabel()
      setShowSubmenu(false);
    };
  }

  function selectSubmenuItem(sectionIndex, sceneIndex, scene) {
    return () => {
      setScenes((draft) => {
        draft[sceneId].section = sectionIndex;
        draft[sceneId].scene = sceneIndex;
      });
      setPreviousSceneLabel(scene.label);
      setShowSubmenu(false);
    };
  }

  const isViewingCurrentProjectOutline = selectedProjects.outline?.projectId === project.id;

  return (
    <StyledContainer
      ref={submenuControllerRef}
      className={classNames({ connected: sceneToWrite })}
    >
      {icon}
      <div
        className="labels"
        onClick={() => setShowIntervention(true)}
      >
        <div className="action-label">{sceneToWrite ? "Outline Linked" : "Not in Outline"}</div>
        <div className="mode-label">{sceneToWrite?.label || previousSceneLabel || ""}</div>
      </div>

      {showSubmenu && (
        <div className="submenu">
          <div
            className="scene main-heading"
            key="choose"
            tabIndex={0}
          >
            <strong>Choose a chapter:</strong>
          </div>
          {!project.useStructuredOutline &&
            Object.values(options).map((section, sectionIndex) => (
              <div className="select-group">
                {Object.values(section.options).map((scene, sceneIndex) => (
                  <div
                    className="scene element"
                    key={sceneIndex}
                    tabIndex={0}
                    role="option"
                    onClick={selectSubmenuItem(sectionIndex, sceneIndex, scene)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        selectSubmenuItem(sectionIndex, sceneIndex, scene)();
                      }
                    }}
                  >
                    {scene.label}
                  </div>
                ))}
              </div>
            ))}
          {project.useStructuredOutline &&
            buildChapterOptions().map((chapter) => {
              return (
                <div
                  className="scene element"
                  key={chapter.value}
                  tabIndex={0}
                  role="option"
                  aria-selected={selectSubmenuItem === chapter.value}
                  onClick={selectStructuredOutlineChapter(chapter.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      selectStructuredOutlineChapter(chapter.value)();
                    }
                  }}
                >
                  {chapter.label}
                </div>
              );
            })}
        </div>
      )}
      {showIntervention && (
        <div
          className="intervention"
          ref={interventionControllerRef}
        >
          {sceneToWrite ? (
            <p>
              This document is linked to <strong>{sceneToWrite?.label}</strong> in your Outline.
            </p>
          ) : (
            <p>
              This document is <strong>not linked</strong> in your Outline.
            </p>
          )}
          <br />
          {sceneToWrite ? <p>Sudowrite will refer to this part of your Outline when generating text.</p> : <p>Sudowrite will not refer to this part of your Outline when generating text.</p>}
          <div className="actions">
            {sceneToWrite && (
              <button
                onClick={() => {
                  setScenes((draft) => {
                    draft[sceneId].section = null;
                    draft[sceneId].scene = null;
                    draft[sceneId].itemId = null;
                  });
                  triggerImmediateSave?.();
                  setShowSubmenu(false);
                  setPreviousSceneLabel("");
                  setShowIntervention(false);
                }}
                className="secondary"
              >
                Unlink
              </button>
            )}
            {(!project.seriesId || isViewingCurrentProjectOutline) && (
              <button
                onClick={() => {
                  setShowSubmenu(true);
                  setShowIntervention(false);
                }}
              >
                Update...
              </button>
            )}
          </div>
        </div>
      )}
    </StyledContainer>
  );
};

export default OutlineSectionSelector;
